import { useState } from "react";
import { LocationMapPreview } from "@/components/location-map-preview";
import { Form, Head } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import SettingsLayout from "@/layouts/settings_layout";
import { locationSettingsLocationPath } from "@/routes";
import type { Location } from "@/types";

type FullLocation = Location & {
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country_code: string;
  proposal_hold_hours: number;
};
type Country = { label: string; value: string; currency: string };
type TimeZone = { label: string; value: string };

export default function LocationSettings({
  location,
  section = "details",
  countries,
  time_zones: timeZones,
  maps_browser_key: mapsBrowserKey,
}: {
  location: FullLocation;
  section?: "details" | "regional" | "booking";
  countries: Country[];
  time_zones: TimeZone[];
  maps_browser_key: string | null;
}) {
  const { t } = useTranslation();
  const [address, setAddress] = useState(location);
  const title = t(
    section === "details"
      ? "navigation.location_details"
      : section === "regional"
        ? "navigation.regional"
        : "navigation.booking_rules",
  );
  return (
    <SettingsLayout>
      <Head title={location.name} />
      <div className="space-y-6">
        <header>
          <h1 className="text-page font-display">{title}</h1>
          <p className="text-muted-foreground text-sm">
            {t(`staff_ux.location_${section}_help`)}
          </p>
        </header>
        <Card className="max-w-3xl">
          <CardContent>
            <Form
              action={locationSettingsLocationPath(location.id, { section })}
              method="patch"
              options={{ preserveScroll: true }}
            >
              {({ errors, processing }) => (
                <FieldGroup>
                  {section === "details" && (
                    <>
                      <Field>
                        <FieldLabel htmlFor="location_name">
                          {t("fields.name")}
                        </FieldLabel>
                        <Input
                          id="location_name"
                          name="location[name]"
                          defaultValue={location.name}
                          required
                        />
                        <FieldError>{errors.name?.[0]}</FieldError>
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="address_1">
                          {t("fields.address_line_1")}
                        </FieldLabel>
                        <Input
                          id="address_1"
                          name="location[address_line_1]"
                          defaultValue={location.address_line_1}
                          onChange={(event) =>
                            setAddress({
                              ...address,
                              address_line_1: event.target.value,
                            })
                          }
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="address_2">
                          {t("fields.address_line_2")}
                        </FieldLabel>
                        <Input
                          id="address_2"
                          name="location[address_line_2]"
                          defaultValue={location.address_line_2}
                          onChange={(event) =>
                            setAddress({
                              ...address,
                              address_line_2: event.target.value,
                            })
                          }
                        />
                      </Field>
                      <div className="grid gap-4 sm:grid-cols-3">
                        <Field>
                          <FieldLabel htmlFor="city">
                            {t("fields.city")}
                          </FieldLabel>
                          <Input
                            id="city"
                            name="location[city]"
                            defaultValue={location.city}
                            onChange={(event) =>
                              setAddress({
                                ...address,
                                city: event.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="region">
                            {t("fields.region")}
                          </FieldLabel>
                          <Input
                            id="region"
                            name="location[region]"
                            defaultValue={location.region}
                            onChange={(event) =>
                              setAddress({
                                ...address,
                                region: event.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="postal">
                            {t("fields.postal_code")}
                          </FieldLabel>
                          <Input
                            id="postal"
                            name="location[postal_code]"
                            defaultValue={location.postal_code}
                            onChange={(event) =>
                              setAddress({
                                ...address,
                                postal_code: event.target.value,
                              })
                            }
                          />
                        </Field>
                      </div>
                    </>
                  )}
                  <div className="grid gap-4 sm:grid-cols-3">
                    {section === "details" && (
                      <Field>
                        <FieldLabel htmlFor="country">
                          {t("fields.country_code")}
                        </FieldLabel>
                        <NativeSelect
                          id="country"
                          name="location[country_code]"
                          defaultValue={location.country_code}
                          onChange={(event) =>
                            setAddress({
                              ...address,
                              country_code: event.target.value,
                            })
                          }
                        >
                          {countries.map((country) => (
                            <NativeSelectOption
                              key={country.value}
                              value={country.value}
                            >
                              {t(`countries.${country.value}`, {
                                defaultValue: country.label,
                              })}
                            </NativeSelectOption>
                          ))}
                        </NativeSelect>
                      </Field>
                    )}
                    {section === "regional" && (
                      <>
                        <Field>
                          <FieldLabel htmlFor="currency">
                            {t("fields.currency")}
                          </FieldLabel>
                          <Input
                            id="currency"
                            name="location[currency]"
                            defaultValue={location.currency}
                            maxLength={3}
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="timezone">
                            {t("fields.time_zone")}
                          </FieldLabel>
                          <NativeSelect
                            id="timezone"
                            name="location[time_zone]"
                            defaultValue={location.time_zone}
                          >
                            {timeZones.map((zone) => (
                              <NativeSelectOption
                                key={zone.value}
                                value={zone.value}
                              >
                                {zone.label}
                              </NativeSelectOption>
                            ))}
                          </NativeSelect>
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="location_locale">
                            {t("common.language")}
                          </FieldLabel>
                          <NativeSelect
                            id="location_locale"
                            name="location[locale]"
                            defaultValue={location.locale}
                          >
                            <NativeSelectOption value="en">
                              English
                            </NativeSelectOption>
                            <NativeSelectOption value="ro">
                              Română
                            </NativeSelectOption>
                          </NativeSelect>
                          <FieldError>{errors.locale?.[0]}</FieldError>
                        </Field>
                      </>
                    )}
                  </div>
                  {section === "booking" && (
                    <Field>
                      <FieldLabel htmlFor="proposal-hold-hours">
                        {t("bays.hold_hours")}
                      </FieldLabel>
                      <Input
                        id="proposal-hold-hours"
                        type="number"
                        min={1}
                        max={8760}
                        step={1}
                        name="location[proposal_hold_hours]"
                        defaultValue={location.proposal_hold_hours}
                        required
                      />
                      <p className="text-muted-foreground text-sm">
                        {t("bays.hold_help")}
                      </p>
                      <FieldError>{errors.proposal_hold_hours}</FieldError>
                    </Field>
                  )}
                  {section === "details" && (
                    <LocationMapPreview
                      browserKey={mapsBrowserKey}
                      address={[
                        address.address_line_1,
                        address.address_line_2,
                        address.city,
                        address.region,
                        address.postal_code,
                        address.country_code,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    />
                  )}
                  {Object.entries(errors)
                    .filter(([key]) => !["name", "locale"].includes(key))
                    .map(([key, messages]) => (
                      <FieldError key={key}>{messages}</FieldError>
                    ))}
                  <Button type="submit" className="w-fit" disabled={processing}>
                    {processing && <Spinner />}
                    {t("common.save")}
                  </Button>
                </FieldGroup>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}
